/**
 * Declarative config of the shared guarantees table (`GuaranteesTableCard`):
 * the row view model, the stable columns and the DataList config builder.
 */

import type { ColumnConfig, DataListConfig } from '@components/integrated/data-list';

import { EMPTY_STATE_TEXT } from './cardText';

/** One guarantee row (view model mapped by the domain service). */
export interface GuaranteeTableRow {
  /** Stable row id (`CD_GAR`, or the row index when absent). */
  id: string;
  /** Guarantee label (`LIB_GAR`). */
  libelle: string | null;
  /** Ceiling in euros (`CAP_GARP`); `null` ⇒ empty cell. */
  plafond: number | null;
  /** Deductible in euros (`FRANCHISE` / `FRA_GARP`); `null` ⇒ empty cell. */
  franchise: number | null;
}

/** Stable column keys. */
export type GuaranteesTableColumnKey = 'libelle' | 'plafond' | 'franchise';

/** Base columns (design order). */
export const GUARANTEES_TABLE_COLUMNS: Readonly<Record<GuaranteesTableColumnKey, ColumnConfig>> = {
  libelle: {
    label: 'Garantie',
    order: 1,
    type: 'text',
    className: 'font-medium',
    sortable: true,
  },
  plafond: {
    label: 'Plafond',
    order: 2,
    type: 'currency',
    typeOptions: { currency: 'EUR', locale: 'fr-FR' },
    align: 'right',
    sortable: true,
  },
  franchise: {
    label: 'Franchise',
    order: 3,
    type: 'currency',
    typeOptions: { currency: 'EUR', locale: 'fr-FR' },
    align: 'right',
    sortable: true,
  },
};

/** Base DataList config of the guarantees table (inline rows, API order). */
export function buildGuaranteesTableConfig(
  componentId: string,
  rows: readonly GuaranteeTableRow[],
): DataListConfig {
  return {
    id: componentId,
    dataSource: { data: rows as unknown as Record<string, unknown>[] },
    columns: { ...GUARANTEES_TABLE_COLUMNS },
    display: { size: 'compact', hoverable: false },
    sorting: { enabled: true },
    toolbar: { visible: false },
    pagination: { mode: 'scroll', pageSize: 999_999 },
    i18n: { emptyMessage: EMPTY_STATE_TEXT },
  };
}
